import { useContactRequests, useRespondToContactRequest } from '@/hooks/mensag/useContacts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { UserPlus, UserX, MessageSquare, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useState } from 'react';

interface ContactRequestsProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ContactRequests({ isOpen, onOpenChange }: ContactRequestsProps) {
  const { data: contactRequests, isLoading } = useContactRequests();
  const respondMutation = useRespondToContactRequest();
  const [view, setView] = useState<'pending' | 'sent' | 'all'>('pending');

  const pendingRequests = contactRequests?.filter(r => r.status === 'pending') || [];
  const sentRequests = contactRequests?.filter(r => r.direction === 'sent') || [];

  const handleResponse = async (requestId: string, fromPubkey: string, response: 'accepted' | 'declined') => {
    try {
      await respondMutation.mutateAsync({
        originalRequestId: requestId,
        fromPubkey: fromPubkey,
        response
      });
    } catch (error) {
      console.error('Error responding to contact request:', error);
    }
  };

  const renderRequests = (requests: typeof pendingRequests, title: string, showActions = false) => (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center space-x-2">
        <Clock className="h-4 w-4" />
        <span>{title} ({requests.length})</span>
      </h3>
      {requests.length === 0 ? (
        <div className="text-center py-8">
          <UserPlus className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No hay solicitudes</p>
        </div>
      ) : (
        requests.map((request) => (
          <Card key={request.id} className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback>
                    {request.direction === 'sent' ? request.toNickname.charAt(0) : request.fromNickname.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">
                        {request.direction === 'sent' ? request.toNickname : request.fromNickname}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {request.direction === 'sent' ? 'Solicitud enviada' : 'Solicitud recibida'}
                      </p>
                    </div>
                    <Badge variant={request.direction === 'sent' ? 'outline' : 'secondary'}>
                      {request.timestamp.toLocaleDateString()}
                    </Badge>
                  </div>
                  {request.message && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">
                      {request.message}
                    </p>
                  )}
                  {showActions && request.direction === 'received' && (
                    <div className="flex space-x-2 mt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResponse(request.id, request.fromPubkey, 'declined')}
                        disabled={respondMutation.isPending}
                        className="flex-1"
                      >
                        <UserX className="h-3 w-3 mr-1" />
                        Declinar
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleResponse(request.id, request.fromPubkey, 'accepted')}
                        disabled={respondMutation.isPending}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        <UserPlus className="h-3 w-3 mr-1" />
                        Aceptar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Solicitudes de Contacto</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Tabs */}
          <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
            <button
              onClick={() => setView('pending')}
              className={`flex-1 px-3 py-1.5 text-sm rounded-md transition ${
                view === 'pending'
                  ? 'bg-white dark:bg-gray-700 shadow text-blue-600 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              Pendientes ({pendingRequests.length})
            </button>
            <button
              onClick={() => setView('sent')}
              className={`flex-1 px-3 py-1.5 text-sm rounded-md transition ${
                view === 'sent'
                  ? 'bg-white dark:bg-gray-700 shadow text-blue-600 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              Enviadas ({sentRequests.length})
            </button>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              {view === 'pending' && renderRequests(pendingRequests, 'Solicitudes Recibidas', true)}
              {view === 'sent' && renderRequests(sentRequests, 'Solicitudes Enviadas')}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}